import React, { useState } from "react";
import { Card, Tooltip } from "antd";
import OnDemandLogo from "../images/OnDemandLogo.png";
import JupyterhubLogo from "../images/JupyterhubLogo.png";
import TensorflowLogo from "../images/TensorflowLogo.png";
import openMlLogo from '../images/openMlLogo.png';
import './CardPageStyles.less';
import { ClipLoader } from 'react-spinners';
import JupyterHubServerModal from "./JupyterHubServerModal";

interface ModelingWorkbenchAppsProps {
    onExternalLinkClick: (url: string, e: React.MouseEvent<HTMLAnchorElement>) => void;
    isTahomaUser: any;
    deleteAllJupyterHubServers: () => Promise<string>;
    getActiveJupyterHubServerList: (server:string) => Promise<boolean>;
}

const ModelingWorkbenchApps: React.FC<ModelingWorkbenchAppsProps> = ({onExternalLinkClick, isTahomaUser, deleteAllJupyterHubServers, getActiveJupyterHubServerList}) => {
    type JupyterHubType = 'datascience' | 'tensorflow';
    const [loadingType, setLoadingType] = useState<JupyterHubType | null>(null);
    const [disabledType, setDisabledType] = useState<JupyterHubType | null>(null);
    const [showexistingServerModal, setShowExistingServerModal] = useState(false);

    const handleExternalLinkClick = (url: string, e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        onExternalLinkClick(url, e);
    };

    const handleOpenOnDemand = (e: any) => {
        // commenting the approach to open url in an iframe and opening in a new tab, as there are few security issues to be addressed.
        // handleExternalLinkClick(process.env.REACT_APP_ONDEMAND as string, e);
        window.open(process.env.REACT_APP_ONDEMAND, '_blank');
    }

    const handleOpenJupyterHub = async (type: JupyterHubType) => {
        setLoadingType(type);
        setDisabledType(type === 'datascience' ? 'tensorflow' : 'datascience');
        const same_server = await getActiveJupyterHubServerList(type);
        if(same_server) {
            setLoadingType(null);
            setDisabledType(null);
            setShowExistingServerModal(true);
        }
        else {
            try {
                const response = await deleteAllJupyterHubServers();
                if (response === 'All servers deleted successfully') {
                    const url = type === 'datascience' ? process.env.REACT_APP_JUPYTERHUB_DATASCIENCE : process.env.REACT_APP_JUPYTERHUB_TENSORFLOW;
                    window.open(url, '_blank');
                } else {
                    console.error("Failed to delete servers or error occurred");
                }
            } catch (error) {
                console.error("Unexpected error occurred", error);
            } finally {
                setLoadingType(null);
                setDisabledType(null);
            }
        }
    };

    const handleLaunch = (type: JupyterHubType) => {
        const url = type === 'datascience' ? process.env.REACT_APP_JUPYTERHUB_DATASCIENCE : process.env.REACT_APP_JUPYTERHUB_TENSORFLOW;
        setShowExistingServerModal(false);
        window.open(url, '_blank');
    }

    const handleOpenAllCards = (e: any) => {
        handleExternalLinkClick('ALL_CARDS', e);
    }

    return (
        <div className="product-brief-intro">
            <div className="title-wrapper">
                <div className="crumbs">
                    <a href="#" onClick={handleOpenAllCards}>All Modules</a> &gt; Modeling Workbench
                </div><br />
                <div className="platform-title">Modeling Workbench</div><br />
                <div className="platform-subtitle">Platform provides a centralized environment for data scientists to build, train, and deploy machine learning models.</div>
                <span style={{ display: 'block', height: '20px' }} />
            </div>
            <div className="card-container-modeling">
                <Tooltip
                    title="A JupyterHub environment fully equipped with a suite of libraries designed for powerful data processing and analysis, supporting Python, R, and Julia."
                    overlayInnerStyle={{color: 'black'}}
                    color="white"
                    placement="bottom"
                >
                    <Card 
                        className={loadingType === 'datascience' || disabledType === 'datascience' ? "disabled app-card" : "app-card"}
                        hoverable
                        onClick={() => handleOpenJupyterHub('datascience')}
                        cover={<img src={JupyterhubLogo} alt="jupyterhub-logo"/>}
                    >
                        {loadingType === 'datascience' && (
                            <div className="spinner-overlay">
                                <ClipLoader color="#000" size={50} />
                            </div>
                        )}
                        <div>
                            <h2 className="title">
                                Data Science
                            </h2>
                            <p className="subtitle">
                                Drive Insights With Multi-language Tools
                            </p>
                        </div>
                    </Card>
                </Tooltip>
                <Tooltip
                    title="A JupyterHub environment designed with all the necessary libraries for conducting deep learning tasks using TensorFlow."
                    overlayInnerStyle={{color: 'black'}}
                    color="white"
                    placement="bottom"
                >
                    <Card 
                        className={loadingType === 'tensorflow' || disabledType === 'tensorflow' ? "disabled app-card" : "app-card"}
                        hoverable
                        onClick={() => handleOpenJupyterHub('tensorflow')}
                        cover={<img src={TensorflowLogo} alt="jupyterhub-logo"/>}
                    >
                        {loadingType === 'tensorflow' && (
                            <div className="spinner-overlay">
                                <ClipLoader color="#000" size={50} />
                            </div>
                        )}
                        <div>
                            <h2 className="title">
                                TensorFlow
                            </h2>
                            <p className="subtitle">
                                Advance Your Deep Learning Journey
                            </p>
                        </div>
                    </Card>
                </Tooltip>
                <Tooltip
                    title= {
                        <div>
                            Workspace to manage files, jobs, run GUI applications, and connect to an interactive shell.
                            {!isTahomaUser && (
                                <>
                                    <br />
                                    <a
                                        href={process.env.REACT_APP_TAHOMA_ACCESS_GUIDE}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: 'blue', textDecoration: 'underline' }}
                                    >
                                        Learn how to get access
                                    </a>
                                </>
                            )}
                        </div>
                    }
                    overlayInnerStyle={{ color: 'black' }}
                    color="white"
                    placement="bottom"
                >
                    <Card
                        className={isTahomaUser ? "app-card" : "disabled app-card"}
                        hoverable
                        onClick={isTahomaUser ? handleOpenOnDemand : undefined}
                        cover={<img src={OnDemandLogo} alt="open-ondemand-logo" />}
                    >
                        <div>
                            <h2 className="title">
                                Tahoma OnDemand
                            </h2>
                            <p className="subtitle">
                                Compute Infrastructure
                            </p>
                            {!isTahomaUser && 
                                <h2>Tahoma account is required.</h2>
                            }
                        </div>
                    </Card>
                </Tooltip>
                <Tooltip
                    title="An open platform for sharing and organizing machine learning datasets, algorithms, and experiments."
                    overlayInnerStyle={{color: 'black'}}
                    color="white"
                    placement="bottom"
                >
                    <Card 
                        className="disabled app-card"
                        cover={<img src={openMlLogo} alt="openML-logo"/>}
                    >
                        <div>
                            <h2 className="title">
                                OpenML
                            </h2>
                            <p className="subtitle">
                                OpenML
                            </p>
                            <p className="typography">Coming in 2025</p>
                        </div>
                    </Card>
                </Tooltip>
            </div>
            {showexistingServerModal && (
                <JupyterHubServerModal
                    isOpen={showexistingServerModal}
                    onClose={() => setShowExistingServerModal(false)}
                    onLaunch={() => {
                        handleLaunch(loadingType as JupyterHubType);
                    }}
                />
            )}
        </div>
    );
  };
  
  export default ModelingWorkbenchApps;
  